/**
 * MultiSelectPicker — searchable multi-select dropdown for entity IDs.
 *
 * Built for the Coupons form to replace the "comma-separated IDs" text input,
 * but generic over any entity with a stable `value` + `label`. Each item can
 * also carry a `secondary` string (e.g. SKU, product count) which is rendered
 * dim under the label AND is included in the search match — so an admin can
 * find a product by name OR by SKU without re-typing.
 *
 * No popover/combobox dependencies: a click-outside listener closes the menu,
 * and the dropdown is positioned absolutely beneath the trigger.
 */
import { Check, ChevronDown, Loader2, Search, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Badge } from './badge';
import { Input } from './input';
import { Label } from './label';

/** Merge two lists of PickerItems by `value`, preserving order from `a`. */
function mergeUnique(a: PickerItem[], b: PickerItem[]): PickerItem[] {
  const seen = new Set(a.map((i) => i.value));
  const merged = a.slice();
  for (const it of b) {
    if (!seen.has(it.value)) {
      seen.add(it.value);
      merged.push(it);
    }
  }
  return merged;
}

export interface PickerItem {
  value: string;
  /** Primary label shown in the dropdown row and on the selected chip. */
  label: string;
  /**
   * Secondary text shown dim beneath the label in the dropdown (NOT on the
   * selected chip). Also matched against the search query so e.g. typing a
   * partial SKU finds the right product.
   */
  secondary?: string;
}

/**
 * Async source for the picker. When provided, the picker switches from a
 * static client-filtered list to a server-driven flow:
 *
 *   - On open (and on query change, debounced 300ms) the picker calls
 *     `fetchPage({ query: '', cursor: null })` to get the first page.
 *   - When the user scrolls to within ~120px of the bottom of the dropdown,
 *     the picker calls `fetchPage({ query, cursor: <last nextCursor> })` to
 *     append the next page. An IntersectionObserver-style scroll listener
 *     handles "infinite" scroll up to whatever the backend will return.
 *   - On mount, if `value` already has selected ids, the picker calls
 *     `hydrate(value)` to fetch labels for the existing chips so an admin
 *     editing an old coupon sees the product names, not raw ObjectIds.
 *
 * The contract is intentionally minimal — the consumer owns the network
 * layer (axios + react-query in our case) and the picker stays UI-only.
 */
export interface PickerAsyncSource {
  /**
   * Fetch one page. `cursor` is `null` for the first page; on subsequent
   * pages the picker passes back the `nextCursor` from the previous call.
   * Return `nextCursor: null` when there are no more pages.
   */
  fetchPage: (params: {
    query: string;
    cursor: string | null;
  }) => Promise<{ items: PickerItem[]; nextCursor: string | null }>;
  /** Resolve labels for already-selected ids. Called once on mount. */
  hydrate?: (ids: string[]) => Promise<PickerItem[]>;
}

interface MultiSelectPickerProps {
  id: string;
  label: string;
  helperText?: string;
  placeholder?: string;
  /** Static list of items. Required when `asyncSource` is omitted. */
  items?: PickerItem[];
  /** Server-driven catalog. When set, `items` is ignored. */
  asyncSource?: PickerAsyncSource;
  value: string[];
  onChange: (next: string[]) => void;
  /** Forced loading state for static mode. Async mode manages its own. */
  loading?: boolean;
  emptyMessage?: string;
  disabled?: boolean;
}

const SCROLL_LOAD_THRESHOLD_PX = 120;
const QUERY_DEBOUNCE_MS = 300;

export function MultiSelectPicker({
  id,
  label,
  helperText,
  placeholder = 'Search…',
  items: staticItems,
  asyncSource,
  value,
  onChange,
  loading: forcedLoading = false,
  emptyMessage = 'No matches',
  disabled = false,
}: MultiSelectPickerProps) {
  const isAsync = Boolean(asyncSource);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  /* ── Async state ──────────────────────────────────────────────────── */
  const [remoteItems, setRemoteItems] = useState<PickerItem[]>([]);
  const [hydratedItems, setHydratedItems] = useState<PickerItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  // Bumped on every new search; old in-flight fetches discard their result.
  const reqIdRef = useRef(0);
  const listElRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  /* ── Async: hydrate selected chips on mount + when value changes  ── */
  // We only hydrate IDs that we don't already have a label for. This is
  // important because hydrate runs whenever `value` changes (e.g. the user
  // selects a new item), and re-fetching just to re-render the freshly
  // selected chip would be wasteful — that chip's label is already in
  // remoteItems from the row click.
  useEffect(() => {
    if (!isAsync || !asyncSource?.hydrate) return;
    if (value.length === 0) {
      setHydratedItems([]);
      return;
    }
    const known = new Set<string>([
      ...remoteItems.map((i) => i.value),
      ...hydratedItems.map((i) => i.value),
    ]);
    const missing = value.filter((v) => !known.has(v));
    if (missing.length === 0) return;
    setHydrating(true);
    const myReq = ++reqIdRef.current;
    void asyncSource
      .hydrate(missing)
      .then((rows) => {
        if (myReq !== reqIdRef.current) return;
        setHydratedItems((prev) => mergeUnique(prev, rows));
      })
      .catch(() => {
        /* leave chips falling back to raw id — the picker still works */
      })
      .finally(() => {
        if (myReq === reqIdRef.current) setHydrating(false);
      });
    // We intentionally don't depend on remoteItems / hydratedItems here —
    // adding them would cause an infinite re-run when we set hydratedItems
    // above. The `known` set is recomputed inside the effect body each call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAsync, value, asyncSource]);

  /* ── Async: fetch first page when picker opens / query changes ───── */
  useEffect(() => {
    if (!isAsync || !asyncSource || !open) return;
    const trimmed = query.trim();
    const myReq = ++reqIdRef.current;
    const t = window.setTimeout(() => {
      setPageLoading(true);
      void asyncSource
        .fetchPage({ query: trimmed, cursor: null })
        .then((res) => {
          if (myReq !== reqIdRef.current) return;
          setRemoteItems(res.items);
          setCursor(res.nextCursor);
          setHasMore(Boolean(res.nextCursor));
        })
        .catch(() => {
          if (myReq !== reqIdRef.current) return;
          setRemoteItems([]);
          setCursor(null);
          setHasMore(false);
        })
        .finally(() => {
          if (myReq === reqIdRef.current) setPageLoading(false);
        });
    }, QUERY_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [isAsync, asyncSource, open, query]);

  /* ── Async: load next page on scroll-to-bottom ──────────────────── */
  const loadNextPage = useCallback(() => {
    if (!isAsync || !asyncSource) return;
    if (pageLoading || !hasMore || !cursor) return;
    const myReq = ++reqIdRef.current;
    setPageLoading(true);
    void asyncSource
      .fetchPage({ query: query.trim(), cursor })
      .then((res) => {
        if (myReq !== reqIdRef.current) return;
        setRemoteItems((prev) => mergeUnique(prev, res.items));
        setCursor(res.nextCursor);
        setHasMore(Boolean(res.nextCursor));
      })
      .catch(() => {
        if (myReq !== reqIdRef.current) return;
        setHasMore(false);
      })
      .finally(() => {
        if (myReq === reqIdRef.current) setPageLoading(false);
      });
  }, [isAsync, asyncSource, cursor, hasMore, pageLoading, query]);

  const onListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!isAsync) return;
      const el = e.currentTarget;
      const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
      if (distanceFromBottom < SCROLL_LOAD_THRESHOLD_PX) loadNextPage();
    },
    [isAsync, loadNextPage]
  );

  /* ── Static items, when not in async mode ────────────────────────── */
  const safeStaticItems = staticItems ?? [];

  // Lookup for chips: union of every source the picker has seen labels for.
  // Async-selected items often live in remoteItems (because the user clicked
  // a visible row), hydrated items cover the rest, and static items cover
  // the static path. Falling back to raw id keeps chips renderable even
  // before any data loads.
  const itemByValue = useMemo(() => {
    const m = new Map<string, PickerItem>();
    for (const it of safeStaticItems) m.set(it.value, it);
    for (const it of remoteItems) m.set(it.value, it);
    for (const it of hydratedItems) m.set(it.value, it);
    return m;
  }, [safeStaticItems, remoteItems, hydratedItems]);

  // In async mode the server already filtered. In static mode we filter
  // client-side over `label + secondary` (same haystack the user types into).
  const visibleItems = useMemo(() => {
    if (isAsync) return remoteItems;
    const q = query.trim().toLowerCase();
    if (!q) return safeStaticItems;
    return safeStaticItems.filter((it) => {
      const haystack = `${it.label} ${it.secondary ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [isAsync, remoteItems, safeStaticItems, query]);

  const loading = forcedLoading || (isAsync && (pageLoading || hydrating));

  const toggle = (val: string) => {
    if (value.includes(val)) onChange(value.filter((v) => v !== val));
    else onChange([...value, val]);
  };

  const removeChip = (val: string) => onChange(value.filter((v) => v !== val));
  const clearAll = () => onChange([]);

  return (
    <div className="grid gap-2" ref={containerRef}>
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        {value.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-muted-foreground hover:text-destructive text-xs transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Selected chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v) => {
            const it = itemByValue.get(v);
            return (
              <Badge key={v} variant="secondary" className="gap-1.5 pl-2 pr-1 font-normal">
                <span className="max-w-[16rem] truncate">{it?.label ?? v}</span>
                <button
                  type="button"
                  onClick={() => removeChip(v)}
                  className="hover:bg-destructive/20 hover:text-destructive rounded-full p-0.5 transition-colors"
                  aria-label={`Remove ${it?.label ?? v}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}

      {/* Trigger */}
      <button
        id={id}
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={`border-input bg-background hover:bg-accent/30 flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
          open ? 'ring-ring ring-2' : ''
        }`}
      >
        <span className="text-muted-foreground">
          {value.length === 0
            ? placeholder
            : `${value.length} selected — click to ${open ? 'close' : 'edit'}`}
        </span>
        <ChevronDown
          className={`text-muted-foreground h-4 w-4 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="relative">
          <div className="border-input bg-popover absolute left-0 right-0 top-1 z-50 max-h-80 overflow-hidden rounded-md border shadow-lg">
            {/* Search row */}
            <div className="border-border flex items-center gap-2 border-b px-3 py-2">
              <Search className="text-muted-foreground h-4 w-4" />
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name or SKU…"
                className="h-7 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="text-muted-foreground hover:bg-accent/50 rounded-full p-0.5"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Options. In async mode the scroll handler triggers next-page
                loading once the user gets within SCROLL_LOAD_THRESHOLD_PX of
                the bottom. */}
            <div ref={listElRef} onScroll={onListScroll} className="max-h-64 overflow-y-auto py-1">
              {loading && visibleItems.length === 0 ? (
                <div className="text-muted-foreground flex items-center justify-center gap-2 px-3 py-6 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : visibleItems.length === 0 ? (
                <div className="text-muted-foreground px-3 py-6 text-center text-sm">
                  {emptyMessage}
                </div>
              ) : (
                <>
                  {visibleItems.map((it) => {
                    const selected = value.includes(it.value);
                    return (
                      <button
                        key={it.value}
                        type="button"
                        onClick={() => toggle(it.value)}
                        className={`hover:bg-accent/40 flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition-colors ${
                          selected ? 'bg-accent/20' : ''
                        }`}
                      >
                        <span
                          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                            selected
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border bg-background'
                          }`}
                        >
                          {selected && <Check className="h-3 w-3" />}
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="text-foreground truncate font-medium">{it.label}</span>
                          {it.secondary && (
                            <span className="text-muted-foreground truncate text-xs">
                              {it.secondary}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                  {/* Async-mode load-more affordance. When there are more pages
                      the spinner shows while the next page is in flight; the
                      onScroll handler also fires `loadNextPage` automatically
                      so the user rarely sees this unless their scroll is fast. */}
                  {isAsync && hasMore && (
                    <div className="text-muted-foreground flex items-center justify-center gap-2 px-3 py-3 text-xs">
                      {pageLoading ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading more…
                        </>
                      ) : (
                        <span>Scroll for more</span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {helperText && <p className="text-muted-foreground text-xs">{helperText}</p>}
    </div>
  );
}
